// ============================================================================
// DAILY BROKERAGE ATTRIBUTION (Bajaj-style)
// ============================================================================
//
// Brokerage is attributed per-day, not per-trade-close:
//   - Buy-side commission books on the day the position is OPENED.
//   - Sell-side commission books on the day the position is CLOSED.
//   - Intraday (same-day buy + sell within 9:15-3:30 IST) → full commission
//     on that single day at the intraday rate.
//   - Any equity/stock position still open at 3:31 PM IST → delivery rate.
//
// This matches Indian retail-broker convention (verified against Bajaj
// Broking, where Ajay Singh's 17-Apr daily brokerage = ₹9,300.26).
//
// Stored `brokerageAmount` on each Trade is unchanged — that field still
// holds the FULL itemized charges (incl. STT, GST, etc.) for the trade and
// is what gets deducted from realizedPnL. These helpers compute the
// commission attribution PER DAY for revenue/dashboard display only.
// ============================================================================

import { TradeType, BrokerageRates } from '../types';
import { istDay } from './formatters';

/** Minimal trade shape needed for daily brokerage attribution. */
export interface TradeForBrokerage {
  tradeType: TradeType;
  date: string;
  saleDate?: string;
  createdAt?: string;
  purchaseLots: number;
  saleLots?: number;
  lotSize: number;
  purchasePrice: number;
  salePrice?: number;
  isDeleted?: boolean;
}

export interface BrokerageContribution {
  buy: number;        // commission for the trade's buy leg, attributed to buy day
  sell: number;       // commission for the trade's sell leg, attributed to sell day
  buyDay: string;     // YYYY-MM-DD IST when the buy happened
  sellDay: string | null;  // YYYY-MM-DD IST when the sell happened (null if open)
  category: 'delivery' | 'intra' | 'future' | 'option';
}

/** What rate to apply for each category — falls back to sensible defaults. */
function pickRate(category: BrokerageContribution['category'], rates: BrokerageRates) {
  switch (category) {
    case 'delivery': return rates.stock ?? 0.003;
    case 'intra':    return rates.intraStock ?? 0.0003;
    case 'future':   return (rates as any).future ?? 0.0003;
    case 'option':   return rates.option ?? 100;
  }
}

/**
 * Compute the buy-leg and sell-leg commission for a single trade, splitting
 * each leg's commission to the day on which it occurred.
 *
 * For Indian intraday (same-day full close on equity), full commission is
 * placed on the sell side (which equals buy side here) so the trade's
 * commission is attributed entirely to that one day at the intraday rate.
 */
export function dailyBrokerageContribution(
  trade: TradeForBrokerage,
  rates: BrokerageRates,
): BrokerageContribution {
  const buyDay = istDay(trade.date || trade.createdAt || '');
  const soldLots = trade.saleLots || 0;
  const sellDay = soldLots > 0
    ? istDay(trade.saleDate || trade.date || trade.createdAt || '')
    : null;

  const buyVal  = trade.purchaseLots * trade.lotSize * trade.purchasePrice;
  const sellVal = soldLots * trade.lotSize * (trade.salePrice || 0);

  // Decide the EFFECTIVE category. An INTRA_STOCK trade still flagged open
  // (no close yet) means it was held past 3:30 PM IST — convention is it
  // becomes delivery. Reclassify for attribution purposes only (we don't
  // mutate stored tradeType).
  let effective = trade.tradeType;
  if (effective === TradeType.INTRA_STOCK && soldLots === 0) {
    effective = TradeType.EQUITY;
  }

  let category: BrokerageContribution['category'];
  let buy = 0;
  let sell = 0;

  if (effective === TradeType.OPTION) {
    category = 'option';
    const perSide = pickRate('option', rates);
    buy  = trade.purchaseLots * perSide;
    sell = soldLots * perSide;
  } else if (effective === TradeType.FUTURE) {
    category = 'future';
    const rate = pickRate('future', rates);
    buy  = buyVal  * rate;
    sell = sellVal * rate;
  } else if (effective === TradeType.INTRA_STOCK) {
    // Closed same-day equity intraday: full commission goes to the (single)
    // close day at the intraday rate. Putting all on `sell` means it
    // attributes correctly when sellDay is the day in question.
    category = 'intra';
    const rate = pickRate('intra', rates);
    buy  = 0;
    sell = (buyVal + sellVal) * rate;
  } else {
    // EQUITY or STOCK delivery — split per side.
    category = 'delivery';
    const rate = pickRate('delivery', rates);
    buy  = buyVal  * rate;
    sell = sellVal * rate;
  }

  return { buy, sell, buyDay, sellDay, category };
}

/** Sum the commission attributed to a single IST day for one trade. */
export function brokerageOnDay(
  trade: TradeForBrokerage,
  dayIST: string,
  rates: BrokerageRates,
): number {
  if (trade.isDeleted) return 0;
  const c = dailyBrokerageContribution(trade, rates);
  let total = 0;
  if (c.buyDay === dayIST) total += c.buy;
  if (c.sellDay === dayIST) total += c.sell;
  return total;
}

/** Sum the commission attributed to any day in [startIST, endIST] for one trade. */
export function brokerageInPeriod(
  trade: TradeForBrokerage,
  startIST: string,
  endIST: string,
  rates: BrokerageRates,
): number {
  if (trade.isDeleted) return 0;
  const c = dailyBrokerageContribution(trade, rates);
  let total = 0;
  if (c.buyDay >= startIST && c.buyDay <= endIST) total += c.buy;
  if (c.sellDay && c.sellDay >= startIST && c.sellDay <= endIST) total += c.sell;
  return total;
}

/** Per-category breakdown for a trade for a single IST day. */
export interface BrokerageBreakdown {
  delivery: number;
  intra: number;
  future: number;
  option: number;
  total: number;
}

export function emptyBreakdown(): BrokerageBreakdown {
  return { delivery: 0, intra: 0, future: 0, option: 0, total: 0 };
}

export function addToBreakdown(
  acc: BrokerageBreakdown,
  trade: TradeForBrokerage,
  dayIST: string,
  rates: BrokerageRates,
): BrokerageBreakdown {
  if (trade.isDeleted) return acc;
  const c = dailyBrokerageContribution(trade, rates);
  let amount = 0;
  if (c.buyDay === dayIST) amount += c.buy;
  if (c.sellDay === dayIST) amount += c.sell;
  if (amount === 0) return acc;
  acc[c.category] += amount;
  acc.total += amount;
  return acc;
}

export function addToBreakdownInPeriod(
  acc: BrokerageBreakdown,
  trade: TradeForBrokerage,
  startIST: string,
  endIST: string,
  rates: BrokerageRates,
): BrokerageBreakdown {
  if (trade.isDeleted) return acc;
  const c = dailyBrokerageContribution(trade, rates);
  let amount = 0;
  if (c.buyDay >= startIST && c.buyDay <= endIST) amount += c.buy;
  if (c.sellDay && c.sellDay >= startIST && c.sellDay <= endIST) amount += c.sell;
  if (amount === 0) return acc;
  acc[c.category] += amount;
  acc.total += amount;
  return acc;
}
